import { create } from 'zustand';
import type { PanelLayout, PanelId } from '../types';
import {
  findNonOverlappingPosition,
  getNavLayout,
  getViewportLayoutConfig,
  organizePanelsResponsive,
  panelLayoutToRect,
  PANEL_ORGANIZE_PRIORITY,
  clampPanelSize,
  clampRectToViewport,
  snapRect,
  rectsOverlap,
  snapToGrid,
  GRID_SIZE,
  getVoiceDockLayout,
  getDefaultChatLayout,
  DEFAULT_CHAT_WIDTH,
  DEFAULT_CHAT_MIN_WIDTH,
  DEFAULT_CHAT_MIN_HEIGHT,
  NAV_SIDEBAR_TOP,
  shouldExpandNavSidebar,
  ensurePanelVisible,
  type ViewportLayoutConfig,
  type LayoutRect,
} from '../canvas/panelLayoutEngine';
import { computeStalePanelActions, AUTO_ORGANIZE_VISIBLE_THRESHOLD } from '../canvas/panelEngagementPolicy';
import { isVoiceCallActive } from '../shared/voiceKernel';
import {
  usePanelEngagementStore,
  type EngagementSource,
} from './panelEngagementStore';
import { useCanvasViewportStore } from './canvasViewportStore';

interface LayoutState {
  panels: Record<string, PanelLayout>;
  pan: { x: number; y: number };
  navCollapsed: boolean;
  /** Mirrors NavSidebar expanded width for voice dock placement. */
  navSidebarExpanded: boolean;
  /** Nav item count for sidebar height → voice dock Y. */
  navSidebarItemCount: number;
  voiceDocked: boolean;
  settingsOpen: boolean;
  snapGrid: boolean;

  // Actions
  showPanel: (id: PanelId, source?: EngagementSource) => void;
  recordPanelEngagement: (id: PanelId, source: EngagementSource) => void;
  bringPanelToFront: (id: PanelId) => void;
  toggleSnapGrid: () => void;
  setSnapGrid: (enabled: boolean) => void;
  hidePanel: (id: PanelId) => void;
  togglePanel: (id: PanelId) => void;
  movePanel: (id: PanelId, x: number, y: number) => void;
  resizePanel: (id: PanelId, w: number, h: number) => void;
  minimizePanel: (id: PanelId) => void;
  maximizePanel: (id: PanelId) => void;
  autoOrganize: () => void;
  panBy: (dx: number, dy: number) => void;
  resetPan: () => void;
  toggleNavCollapsed: () => void;
  setNavSidebarExpanded: (expanded: boolean) => void;
  setNavSidebarItemCount: (count: number) => void;
  toggleSettings: () => void;
  setPanelPosition: (id: PanelId, x: number, y: number) => void;
  updateVoiceDock: () => void;
  reclampVisiblePanels: () => void;
}

function getViewportForLayout(navSidebarExpanded: boolean): ViewportLayoutConfig {
  const { width, height } = useCanvasViewportStore.getState();
  return getViewportLayoutConfig({
    viewportWidth: width,
    viewportHeight: height,
    navSidebarExpanded,
  });
}

function buildObstacleRects(
  panels: Record<string, PanelLayout>,
  excludeId: string,
): ReturnType<typeof panelLayoutToRect>[] {
  return Object.entries(panels)
    .filter(([pid, p]) => pid !== excludeId && p.visible)
    .map(([, p]) => panelLayoutToRect(p));
}

function rectOverlapsObstacles(
  rect: LayoutRect,
  obstacles: LayoutRect[],
  gap: number,
): boolean {
  return obstacles.some((obstacle) => rectsOverlap(rect, obstacle, gap));
}

function fitPanelDimensionsForPlacement(
  panelW: number,
  panelH: number,
  obstacles: LayoutRect[],
  viewport: ViewportLayoutConfig,
  minW: number,
  minH: number,
  snapGrid: boolean,
): { w: number; h: number } {
  const pos = findNonOverlappingPosition(panelW, panelH, obstacles, viewport, {
    snapGrid,
  });
  if (
    !rectOverlapsObstacles(
      { x: pos.x, y: pos.y, w: panelW, h: panelH },
      obstacles,
      viewport.gap,
    )
  ) {
    return { w: panelW, h: panelH };
  }

  let fittedW = panelW;
  let fittedH = panelH;

  for (const obstacle of obstacles) {
    const besideW = viewport.right - (obstacle.x + obstacle.w + viewport.gap);
    if (besideW >= minW) {
      fittedW = Math.min(fittedW, besideW);
    }

    const belowH = viewport.bottom - (obstacle.y + obstacle.h + viewport.gap);
    if (belowH >= minH) {
      fittedH = Math.min(fittedH, belowH);
    }
  }

  const clamped = clampPanelSize(fittedW, fittedH, minW, minH, viewport);
  if (snapGrid) {
    return {
      w: Math.max(GRID_SIZE, snapToGrid(clamped.w)),
      h: Math.max(GRID_SIZE, snapToGrid(clamped.h)),
    };
  }

  return clamped;
}

function placePanelInViewport(
  panel: PanelLayout,
  panels: Record<string, PanelLayout>,
  openingId: string,
  navSidebarExpanded: boolean,
  snapGrid: boolean,
): PanelLayout {
  const viewport = getViewportForLayout(navSidebarExpanded);
  const minW = panel.minW ?? 280;
  const minH = panel.minH ?? 200;
  const obstacles = buildObstacleRects(panels, openingId);
  const clamped = clampPanelSize(
    panel.w ?? 400,
    panel.h ?? 300,
    minW,
    minH,
    viewport,
  );
  const { w: newW, h: newH } = fitPanelDimensionsForPlacement(
    clamped.w,
    clamped.h,
    obstacles,
    viewport,
    minW,
    minH,
    snapGrid,
  );
  const { x: rawX, y: rawY } = findNonOverlappingPosition(
    newW,
    newH,
    obstacles,
    viewport,
    { snapGrid },
  );

  let rect: LayoutRect = clampRectToViewport(
    { x: rawX, y: rawY, w: newW, h: newH },
    viewport,
    minW,
    minH,
  );
  if (snapGrid) {
    rect = snapRect(rect);
    rect = clampRectToViewport(rect, viewport, minW, minH);
    if (rectOverlapsObstacles(rect, obstacles, viewport.gap)) {
      const repositioned = findNonOverlappingPosition(
        rect.w,
        rect.h,
        obstacles,
        viewport,
        { snapGrid: true },
      );
      rect = clampRectToViewport(
        {
          ...rect,
          x: repositioned.x,
          y: repositioned.y,
        },
        viewport,
        minW,
        minH,
      );
      if (rectOverlapsObstacles(rect, obstacles, viewport.gap)) {
        const verified = findNonOverlappingPosition(
          rect.w,
          rect.h,
          obstacles,
          viewport,
          { snapGrid: true },
        );
        rect = {
          ...rect,
          x: verified.x,
          y: verified.y,
        };
      }
    }
  }

  rect = clampRectToViewport(rect, viewport, minW, minH);

  return {
    ...panel,
    visible: true,
    x: rect.x,
    y: rect.y,
    w: rect.w,
    h: rect.h,
    minimized: false,
  };
}

function countVisibleContentPanels(
  panels: Record<string, PanelLayout>,
): number {
  return Object.entries(panels).filter(
    ([pid, p]) => pid !== 'nav' && p.visible,
  ).length;
}

function organizeVisiblePanelsInPlace(
  panels: Record<string, PanelLayout>,
  navSidebarExpanded: boolean,
): Record<string, PanelLayout> {
  const viewport = getViewportForLayout(navSidebarExpanded);

  const visibleContentPanels = Object.entries(panels)
    .filter(([pid, p]) => {
      if (pid === 'nav' || !p.visible) return false;
      // Keep voice geometry stable for the session lifetime — auto-organize
      // must not reposition the panel host while mic/WebSocket are live.
      if (isVoiceCallActive() && pid === 'voice') return false;
      return true;
    })
    .map(([pid, p]) => ({
      id: pid,
      w: p.w ?? 400,
      h: p.h ?? 300,
      minW: p.minW ?? 280,
      minH: p.minH ?? 200,
      priority: PANEL_ORGANIZE_PRIORITY[pid] ?? 200,
    }));

  if (visibleContentPanels.length === 0) return panels;

  const organized = organizePanelsResponsive(visibleContentPanels, viewport, {
    snapGrid: true,
  });

  const updatedPanels: Record<string, PanelLayout> = { ...panels };

  for (const [pid, layout] of Object.entries(organized)) {
    const existing = panels[pid];
    if (!existing) continue;
    updatedPanels[pid] = {
      ...existing,
      x: layout.x,
      y: layout.y,
      w: layout.w,
      h: layout.h,
      visible: true,
      minimized: false,
    };
  }

  return updatedPanels;
}

function applyAutoOrganizeIfNeeded(
  panels: Record<string, PanelLayout>,
  navSidebarExpanded: boolean,
): Record<string, PanelLayout> {
  if (countVisibleContentPanels(panels) < AUTO_ORGANIZE_VISIBLE_THRESHOLD) {
    return panels;
  }
  return organizeVisiblePanelsInPlace(panels, navSidebarExpanded);
}

function applyStalePanelActions(
  panels: Record<string, PanelLayout>,
  openingId: PanelId,
): Record<string, PanelLayout> {
  const snapshot = usePanelEngagementStore.getState().toSnapshot();
  const actions = computeStalePanelActions(panels, openingId, snapshot);
  if (actions.length === 0) return panels;

  const next = { ...panels };
  for (const action of actions) {
    const existing = next[action.panelId];
    if (!existing) continue;
    if (action.type === 'minimize') {
      next[action.panelId] = { ...existing, minimized: true };
    } else {
      next[action.panelId] = { ...existing, visible: false };
    }
  }
  return next;
}

function recordEngagement(id: PanelId, source: EngagementSource): void {
  usePanelEngagementStore.getState().recordEngagement(id, source);
  usePanelEngagementStore.getState().bringToFront(id);
}

function voiceRectMatches(
  panel: PanelLayout,
  rect: { x: number; y: number; w: number; h: number },
): boolean {
  return (
    panel.x === rect.x &&
    panel.y === rect.y &&
    panel.w === rect.w &&
    panel.h === rect.h
  );
}

function applyChatDefaultLayout(
  chat: PanelLayout,
  navSidebarExpanded: boolean,
  navSidebarItemCount: number,
  snapGrid: boolean,
): PanelLayout {
  const viewport = getViewportForLayout(navSidebarExpanded);
  const minW = chat.minW ?? DEFAULT_CHAT_MIN_WIDTH;
  const minH = chat.minH ?? DEFAULT_CHAT_MIN_HEIGHT;

  let rect = getDefaultChatLayout({
    viewport,
    navSidebarExpanded,
    sidebarItemCount: navSidebarItemCount,
    snapGrid,
  });
  rect = clampRectToViewport(rect, viewport, minW, minH);
  if (snapGrid) {
    rect = snapRect(rect);
    rect = clampRectToViewport(rect, viewport, minW, minH);
  }

  return {
    ...chat,
    x: rect.x,
    y: rect.y,
    w: rect.w,
    h: rect.h,
  };
}

const defaultLayouts: Record<string, PanelLayout> = {
  nav: { x: 16, y: 64, w: 200, h: 320, visible: true, autoHeight: true, resizable: false },
  chat: {
    x: 242,
    y: NAV_SIDEBAR_TOP,
    w: DEFAULT_CHAT_WIDTH,
    h: 280,
    visible: true,
    resizable: true,
    minW: DEFAULT_CHAT_MIN_WIDTH,
    minH: DEFAULT_CHAT_MIN_HEIGHT,
  },
  artifacts: { x: 800, y: 72, w: 420, h: 340, visible: false, resizable: true, minW: 360, minH: 280 },
  'open-positions': { x: 240, y: 72, w: 640, h: 560, visible: false, resizable: true, minW: 500, minH: 420 },
  applications: { x: 260, y: 72, w: 560, h: 520, visible: false, resizable: true, minW: 460, minH: 380 },
  resources: { x: 260, y: 72, w: 560, h: 520, visible: false, resizable: true, minW: 460, minH: 380 },
  'career-tools': { x: 260, y: 72, w: 560, h: 520, visible: false, resizable: true, minW: 460, minH: 380 },
  'growth-paths': { x: 240, y: 72, w: 880, h: 540, visible: false, resizable: true, minW: 700, minH: 420 },
  'career-trajectories': { x: 240, y: 72, w: 900, h: 420, visible: false, resizable: true, minW: 700, minH: 320 },
  voice: { x: 20, y: 520, w: 280, h: 200, visible: false, resizable: false },
  journey: { x: 800, y: 440, w: 420, h: 300, visible: false, resizable: true, minW: 360, minH: 240 },
  settings: { x: 400, y: 200, w: 480, h: 400, visible: false, resizable: false },
};

function clampDefaultsToViewport(
  layouts: Record<string, PanelLayout>,
  navSidebarExpanded: boolean,
  snapGrid: boolean,
  navSidebarItemCount: number = 7,
): Record<string, PanelLayout> {
  if (typeof window === 'undefined') return layouts;

  const out: Record<string, PanelLayout> = { ...layouts };
  const navLayout = getNavLayout(navSidebarExpanded);
  out.nav = {
    ...out.nav,
    x: navLayout.x,
    y: navLayout.y,
    w: navLayout.w,
  };

  if (out.chat?.visible) {
    out.chat = applyChatDefaultLayout(
      out.chat,
      navSidebarExpanded,
      navSidebarItemCount,
      snapGrid,
    );
  }

  for (const [id, panel] of Object.entries(out)) {
    if (id === 'nav' || id === 'chat' || !panel.visible) continue;
    out[id] = placePanelInViewport(
      panel,
      out,
      id,
      navSidebarExpanded,
      snapGrid,
    );
  }

  return out;
}

function readInitialNavSidebarExpanded(): boolean {
  if (typeof window === 'undefined') return true;
  return shouldExpandNavSidebar(useCanvasViewportStore.getState().width);
}

const initialNavSidebarExpanded = readInitialNavSidebarExpanded();
const initialSnapGrid = true;

export const useLayoutStore = create<LayoutState>((set, get) => ({
  panels: clampDefaultsToViewport(
    { ...defaultLayouts },
    initialNavSidebarExpanded,
    initialSnapGrid,
    7,
  ),
  pan: { x: 0, y: 0 },
  navCollapsed: false,
  navSidebarExpanded: initialNavSidebarExpanded,
  navSidebarItemCount: 7,
  voiceDocked: false,
  settingsOpen: false,
  snapGrid: true,

  recordPanelEngagement: (id: PanelId, source: EngagementSource) => {
    recordEngagement(id, source);
  },

  bringPanelToFront: (id: PanelId) => {
    recordEngagement(id, 'focus');
  },

  toggleSnapGrid: () => {
    set((state) => ({ snapGrid: !state.snapGrid }));
  },

  setSnapGrid: (enabled: boolean) => {
    set({ snapGrid: enabled });
  },

  showPanel: (id: PanelId, source: EngagementSource = 'open') => {
    recordEngagement(id, source);
    set((state) => {
      const panel = state.panels[id];
      if (!panel) {
        console.warn(`Panel "${id}" not found in layoutStore`);
        return state;
      }

      const viewport = getViewportForLayout(state.navSidebarExpanded);
      const pruned = applyStalePanelActions(state.panels, id);
      const placed = placePanelInViewport(
        pruned[id] ?? panel,
        pruned,
        id,
        state.navSidebarExpanded,
        state.snapGrid,
      );
      const nextPanels = { ...pruned, [id]: placed };
      const organized = applyAutoOrganizeIfNeeded(
        nextPanels,
        state.navSidebarExpanded,
      );
      const pan = ensurePanelVisible(
        panelLayoutToRect(organized[id] ?? placed),
        state.pan,
        viewport,
      );

      return { panels: organized, pan };
    });
    get().updateVoiceDock();
  },

  hidePanel: (id: PanelId) => {
    set((state) => ({
      panels: {
        ...state.panels,
        [id]: { ...state.panels[id], visible: false },
      },
    }));
    get().updateVoiceDock();
  },

  togglePanel: (id: PanelId) => {
    const panel = get().panels[id];
    if (!panel) {
      console.warn(`Panel "${id}" not found in layoutStore`);
      return;
    }

    if (panel.visible) {
      set((state) => ({
        panels: {
          ...state.panels,
          [id]: { ...state.panels[id], visible: false },
        },
      }));
      get().updateVoiceDock();
      return;
    }

    recordEngagement(id, 'toggle-open');
    set((state) => {
      const current = state.panels[id];
      if (!current) return state;

      const viewport = getViewportForLayout(state.navSidebarExpanded);
      const pruned = applyStalePanelActions(state.panels, id);
      const placed = placePanelInViewport(
        pruned[id] ?? current,
        pruned,
        id,
        state.navSidebarExpanded,
        state.snapGrid,
      );
      const nextPanels = { ...pruned, [id]: placed };
      const organized = applyAutoOrganizeIfNeeded(
        nextPanels,
        state.navSidebarExpanded,
      );
      const pan = ensurePanelVisible(
        panelLayoutToRect(organized[id] ?? placed),
        state.pan,
        viewport,
      );

      return { panels: organized, pan };
    });
    get().updateVoiceDock();
  },

  movePanel: (id: PanelId, x: number, y: number) => {
    set((state) => ({
      panels: {
        ...state.panels,
        [id]: { ...state.panels[id], x, y },
      },
    }));
  },

  resizePanel: (id: PanelId, w: number, h: number) => {
    set((state) => ({
      panels: {
        ...state.panels,
        [id]: { ...state.panels[id], w, h },
      },
    }));
  },

  minimizePanel: (id: PanelId) => {
    set((state) => ({
      panels: {
        ...state.panels,
        [id]: { ...state.panels[id], minimized: true },
      },
    }));
  },

  maximizePanel: (id: PanelId) => {
    set((state) => ({
      panels: {
        ...state.panels,
        [id]: { ...state.panels[id], minimized: false },
      },
    }));
  },

  autoOrganize: () => {
    set((state) => {
      const navLayout = getNavLayout(state.navSidebarExpanded);
      const organized = organizeVisiblePanelsInPlace(
        state.panels,
        state.navSidebarExpanded,
      );

      const updatedPanels: Record<string, PanelLayout> = { ...organized };

      updatedPanels.nav = {
        ...state.panels.nav,
        x: navLayout.x,
        y: navLayout.y,
        w: navLayout.w,
        visible: true,
      };

      return {
        panels: updatedPanels,
        pan: { x: 0, y: 0 },
      };
    });
  },

  panBy: (dx: number, dy: number) => {
    set((state) => ({
      pan: { x: state.pan.x + dx, y: state.pan.y + dy },
    }));
  },

  resetPan: () => {
    set({ pan: { x: 0, y: 0 } });
  },

  toggleNavCollapsed: () => {
    set((state) => ({ navCollapsed: !state.navCollapsed }));
  },

  setNavSidebarExpanded: (expanded: boolean) => {
    set({ navSidebarExpanded: expanded });
    get().reclampVisiblePanels();
    get().updateVoiceDock();
  },

  setNavSidebarItemCount: (count: number) => {
    const next = Math.max(0, count);
    set((state) => {
      if (state.navSidebarItemCount === next) return state;
      return { navSidebarItemCount: next };
    });
    get().updateVoiceDock();
  },

  updateVoiceDock: () => {
    if (typeof window === 'undefined') return;
    set((state) => {
      const voice = state.panels.voice;
      if (!voice?.visible) {
        return state.voiceDocked ? { voiceDocked: false } : state;
      }

      const dockLayout = getVoiceDockLayout({
        navSidebarExpanded: state.navSidebarExpanded,
        sidebarItemCount: state.navSidebarItemCount,
      });

      // During an active call, freeze geometry after the first dock snap so
      // layout churn does not remount VoiceWidget / tear down Gemini Live.
      if (isVoiceCallActive() && state.voiceDocked) {
        return state;
      }

      if (state.voiceDocked && voiceRectMatches(voice, dockLayout)) {
        return state;
      }

      const nextPanels = {
        ...state.panels,
        voice: {
          ...voice,
          x: dockLayout.x,
          y: dockLayout.y,
          w: dockLayout.w,
          h: dockLayout.h,
        },
      };

      return {
        voiceDocked: true,
        panels: nextPanels,
      };
    });
  },

  reclampVisiblePanels: () => {
    set((state) => {
      const viewport = getViewportForLayout(state.navSidebarExpanded);
      let changed = false;
      const nextPanels = { ...state.panels };

      for (const [id, panel] of Object.entries(state.panels)) {
        if (!panel.visible || id === 'nav') continue;
        const minW = panel.minW ?? 280;
        const minH = panel.minH ?? 200;
        let rect = panelLayoutToRect(panel);
        rect = clampRectToViewport(rect, viewport, minW, minH);
        if (state.snapGrid) {
          rect = snapRect(rect);
          rect = clampRectToViewport(rect, viewport, minW, minH);
        }

        const currentW = panel.w ?? 400;
        const currentH = panel.h ?? 300;
        if (
          rect.x !== panel.x ||
          rect.y !== panel.y ||
          rect.w !== currentW ||
          rect.h !== currentH
        ) {
          changed = true;
          nextPanels[id] = {
            ...panel,
            x: rect.x,
            y: rect.y,
            w: rect.w,
            h: rect.h,
          };
        }
      }

      if (!changed) return state;
      return { panels: nextPanels };
    });
  },

  toggleSettings: () => {
    set((state) => ({ settingsOpen: !state.settingsOpen }));
  },

  setPanelPosition: (id: PanelId, x: number, y: number) => {
    set((state) => ({
      panels: {
        ...state.panels,
        [id]: { ...state.panels[id], x, y },
      },
    }));
  },
}));
